// dependencies
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@performyard/py-components";

// queries
import { companyEngagementSettingsQuery, companySettingsQuery } from "../../queries";
// api services
import {
	ICompany,
	UpdateSessionSettingsRequest,
	getCompanyFields,
	getCompanyLicenses,
	updateCompanySessionSettings,
} from "../../services/defs/company";

export const useCompanySettings = () => {
	return useQuery(companySettingsQuery());
};

export const useCompanyFunction = (
	functionName: string
): {
	companyFunction: "enabled" | "disabled" | "hidden";
	attributes: ICompany["functions"][string]["attributes"];
	isLoading: boolean;
} => {
	const { data: companySettings, isLoading } = useCompanySettings();
	return {
		companyFunction: companySettings?.functions[functionName]?.state || "disabled",
		attributes: companySettings?.functions[functionName]?.attributes || {},
		isLoading,
	};
};

export const useCompanyLicenses = () => {
	return useQuery({
		queryKey: ["company-license"],
		queryFn: () => getCompanyLicenses(),
	});
};

export const useCompanyEngagementSettings = () => {
	return useQuery(companyEngagementSettingsQuery());
};

export const useCompanyBasic = () => {
	const { data: companySettings, isLoading } = useCompanySettings();
	return { company: companySettings?.company || null, isLoading };
};

export const useCompanyFields = (companyId: string) => {
	return useQuery({
		queryKey: ["company-fields", companyId],
		queryFn: () => getCompanyFields(companyId),
		meta: {
			errorMessage: "There was an error getting the company fields.",
		},
		enabled: !!companyId && companyId !== "",
	});
};

/**
 * Hook for updating company session timeout settings.
 * Uses optimistic updates to prevent UI flashing and shows success toast.
 */
export const useUpdateCompanySessionSettings = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: UpdateSessionSettingsRequest) => updateCompanySessionSettings(data),
		onMutate: async (newData) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: ["company-settings"] });

			// Snapshot the previous value
			const previousSettings = queryClient.getQueryData(["company-settings"]);

			// Optimistically update to the new value
			if (previousSettings && newData.sessions) {
				queryClient.setQueryData(["company-settings"], (old: any) => {
					const updated = { ...old };
					if (newData.sessions?.timeout !== undefined) {
						updated.authentication.sessionTimeout = newData.sessions.timeout;
					}
					if (newData.sessions?.redirect !== undefined) {
						updated.authentication.sessionTimeoutRedirect = newData.sessions.redirect;
					}
					return updated;
				});
			}

			// Return a context object with the snapshotted value
			return { previousSettings };
		},
		onError: (err, newData, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousSettings) {
				queryClient.setQueryData(["company-settings"], context.previousSettings);
			}
		},
		onSuccess: () => {
			toast.success("Settings updated successfully");
		},
		onSettled: () => {
			// Always refetch after error or success to ensure we have correct data
			queryClient.invalidateQueries({ queryKey: ["company-settings"] });
		},
		meta: {
			errorMessage: "Unable to update settings",
		},
	});
};
